import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiChevronRight,
  FiCreditCard,
  FiShield
} from 'react-icons/fi';
import './CheckoutStyles.css';
import RazorpayPayment from './RazorpayPayment';

const formatCurrency = (amount = 0) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const ALL_PAYMENT_METHODS = [
  {
    id: 'razorpay',
    name: 'Online Payment',
    subtitle: 'Cards, UPI, Netbanking',
    description: 'Pay securely with card, UPI, netbanking, or wallet.',
    highlight: 'Instant confirmation'
  }
];

const PaymentForm = ({
  onPaymentMethodSelect,
  selectedPaymentMethod,
  onBack,
  totalAmount,
  itemsPrice = 0,
  shippingPrice = 0,
  taxPrice = 0,
  order,
  onCreatePaymentPayload,
  onPaymentFailed
}) => {
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();

  const renderMethodIcon = () => <FiCreditCard />;

  const handlePaymentMethodChange = (method) => {
    if (paymentSuccess || selectedPaymentMethod?.id === method.id) {
      return;
    }

    setPaymentError('');
    onPaymentMethodSelect(method);
  };

  const handlePaymentSuccess = (paidOrder, paymentMethod) => {
    const finalOrder = paidOrder || order;
    const finalPaymentMethod = paymentMethod || selectedPaymentMethod || {
      id: 'razorpay',
      name: 'Online Payment'
    };

    setPaymentSuccess(true);
    setPaymentData(finalOrder);

    setTimeout(() => {
      navigate('/order-confirmation', {
        state: {
          order: finalOrder,
          paymentMethod: finalPaymentMethod
        }
      });
    }, 1200);
  };

  const selectedMethodName = selectedPaymentMethod?.name || 'Select method';

  return (
    <div className="payment-form-container payment-form-container--simple">
      <div className="payment-simple-topbar">
        <button
          type="button"
          className="payment-shell-back"
          onClick={onBack}
          disabled={paymentSuccess}
        >
          Back to Address
        </button>

        <div className="payment-simple-total" aria-label="Amount to pay">
          <span>Amount to pay</span>
          <strong>{formatCurrency(totalAmount)}</strong>
        </div>
      </div>

      <div className="payment-layout-shell payment-layout-shell--simple">
        <section className="payment-choice-panel">
          <div className="payment-simple-header">
            <span className="payment-form-kicker">Payment</span>
            <h2>Choose how to pay</h2>
            <p>Select the online payment option to continue securely.</p>
          </div>

          <div className="payment-method-grid payment-method-grid--simple">
            {ALL_PAYMENT_METHODS.map((method) => {
              const isSelected = selectedPaymentMethod?.id === method.id;
              const statusText = isSelected ? 'Selected' : method.highlight;

              return (
                <button
                  type="button"
                  key={method.id}
                  className={`payment-method-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handlePaymentMethodChange(method)}
                  aria-pressed={isSelected}
                >
                  <span className="payment-icon-shell">
                    {renderMethodIcon()}
                  </span>

                  <span className="payment-info">
                    <span className="payment-method-subtitle">{method.subtitle}</span>
                    <span className="payment-name">{method.name}</span>
                    <span className="payment-description">{method.description}</span>
                  </span>

                  <span className={`payment-method-status-chip ${isSelected ? 'payment-method-status-chip--selected' : ''}`}>
                    {statusText}
                  </span>

                  <span className={`payment-method-check ${isSelected ? 'selected' : ''}`}>
                    {isSelected ? <FiCheckCircle /> : <FiChevronRight />}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="payment-summary-card payment-summary-card--compact">
          <div className="payment-summary-card-header">
            <div>
              <h3>Order Summary</h3>
              <p>Check the amount before you continue.</p>
            </div>
            <span className="payment-summary-method-pill">
              {selectedMethodName}
            </span>
          </div>

          <div className="payment-summary-list">
            <div className="payment-summary-row">
              <span>Items total</span>
              <strong>{formatCurrency(itemsPrice || totalAmount)}</strong>
            </div>
            <div className="payment-summary-row">
              <span>Delivery</span>
              <strong>{formatCurrency(shippingPrice)}</strong>
            </div>
            {taxPrice > 0 ? (
              <div className="payment-summary-row">
                <span>Taxes</span>
                <strong>{formatCurrency(taxPrice)}</strong>
              </div>
            ) : null}
            <div className="payment-summary-total">
              <span>Amount to pay</span>
              <strong>{formatCurrency(totalAmount)}</strong>
            </div>
          </div>

          <div className="payment-summary-note">
            <FiShield />
            <p>Your selected method and total stay clear before checkout.</p>
          </div>
        </aside>

        <section className="payment-main-panel payment-main-panel--simple">
          {selectedPaymentMethod?.id === 'razorpay' ? (
            onCreatePaymentPayload ? (
              <RazorpayPayment
                totalAmount={totalAmount}
                itemsPrice={itemsPrice}
                shippingPrice={shippingPrice}
                taxPrice={taxPrice}
                selectedPaymentMethod={selectedPaymentMethod}
                onCreatePaymentPayload={onCreatePaymentPayload}
                onSuccess={(paidOrder, paymentMethod) => {
                  handlePaymentSuccess(paidOrder, paymentMethod);
                }}
                onError={(errorPayload) => {
                  const paymentState = typeof errorPayload === 'object' && errorPayload?.paymentState
                    ? errorPayload.paymentState
                    : 'failed';
                  const errorMessage = typeof errorPayload === 'object'
                    ? errorPayload?.message
                    : errorPayload;

                  setPaymentError(
                    errorMessage ||
                    (paymentState === 'incomplete'
                      ? 'Payment not completed. Please proceed again.'
                      : 'Payment failed. Please try again.')
                  );

                  if (onPaymentFailed) {
                    onPaymentFailed({
                      paymentState,
                      message: errorMessage
                    });
                  }
                }}
                onBack={() => onPaymentMethodSelect(null)}
              />
            ) : (
              <div className="payment-stage-panel payment-stage-panel--empty payment-stage-panel--compact">
                <div className="payment-stage-intro payment-stage-intro--simple">
                  <span className="payment-stage-badge">Online Payment</span>
                  <h3>Preparing secure payment</h3>
                  <p>Your order is being prepared for online payment. Please wait a moment.</p>
                </div>
              </div>
            )
          ) : (
            <div className="payment-stage-panel payment-stage-panel--compact payment-stage-panel--empty">
              <div className="payment-stage-intro payment-stage-intro--simple">
                <span className="payment-stage-badge">Next Step</span>
                <h3>Select a payment option</h3>
                <p>Choose Online Payment above to continue.</p>
              </div>

              <div className="payment-stage-actions">
                <button
                  type="button"
                  className="back-button"
                  onClick={onBack}
                  disabled={paymentSuccess}
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {paymentError ? (
            <div className="payment-error-message">
              <p><strong>Payment Error:</strong> {paymentError}</p>
              <p>Please check your payment details and try again.</p>
            </div>
          ) : null}
        </section>
      </div>

      {paymentSuccess ? (
        <div className="payment-success-overlay">
          <div className="payment-success-content">
            <div className="success-icon"><FiCheckCircle /></div>
            <h3>Payment Successful!</h3>
            <p>Your payment has been processed successfully.</p>
            <p>Order ID: {paymentData?.orderId || paymentData?._id || 'N/A'}</p>
            <p>Payment ID: {paymentData?.paymentId || paymentData?.paymentResult?.id || 'N/A'}</p>
            <p className="payment-note">Redirecting to order confirmation...</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentForm;
